import type {
  Service, Pod, Trace, Incident, Project, GlobalSearchResult, GlobalSearchResponse,
} from "@coremonit/types";

function scoreMatch(text: string, q: string): number {
  const t = text.toLowerCase();
  const needle = q.toLowerCase();
  if (t === needle) return 100;
  if (t.startsWith(needle)) return 85;
  if (t.includes(needle)) return 60;
  // fuzzy: all chars in order
  let ti = 0;
  for (const ch of needle) {
    ti = t.indexOf(ch, ti);
    if (ti < 0) return 0;
    ti++;
  }
  return 35;
}

export function globalSearch(
  query: string,
  services: Service[],
  podsByService: Map<string, Pod[]>,
  traces: Trace[],
  incidents: Incident[],
  projects: Project[],
  limit = 30,
): GlobalSearchResponse {
  const q = query.trim();
  if (!q) {
    return { query: q, items: [], total: 0, facets: {} };
  }

  const items: GlobalSearchResult[] = [];

  for (const p of projects) {
    const s = scoreMatch(`${p.name} ${p.key} ${p.description}`, q);
    if (s > 0) items.push({
      id: p.id, kind: "project", title: p.name, subtitle: `Project · ${p.key}`,
      path: `/projects/${p.key}`, score: s, health: p.status, tags: [p.key],
    });
  }

  for (const svc of services) {
    const s = scoreMatch(`${svc.name} ${svc.team} ${svc.tags.join(" ")} ${svc.deployment.cluster ?? ""}`, q);
    if (s > 0) items.push({
      id: svc.id, kind: "microservice", title: svc.name,
      subtitle: `${svc.team} · ${svc.environment} · ${svc.deployment.cluster ?? "—"}`,
      path: `/services/${svc.id}`, score: s, health: svc.status, tags: svc.tags,
    });
  }

  for (const [svcId, pods] of podsByService) {
    const svc = services.find((s) => s.id === svcId);
    for (const pod of pods) {
      const s = scoreMatch(pod.name, q);
      if (s > 0) items.push({
        id: pod.id, kind: "pod", title: pod.name,
        subtitle: `${svc?.name ?? svcId} · ${pod.namespace}`,
        path: `/containers/${svcId}/${pod.id}`, score: s,
        health: pod.status === "running" ? "healthy" : "warning",
      });
      for (const c of [...pod.containers, ...pod.sidecars]) {
        const cs = scoreMatch(c.name, q);
        if (cs > 0) items.push({
          id: `${pod.id}_${c.name}`, kind: "container", title: c.name,
          subtitle: `Container · ${pod.name}`,
          path: `/containers/${svcId}/${pod.id}/${encodeURIComponent(c.name)}`, score: cs,
        });
      }
    }
  }

  const clusters = [...new Set(services.map((s) => s.deployment.cluster).filter(Boolean))] as string[];
  for (const c of clusters) {
    const s = scoreMatch(c, q);
    if (s > 0) items.push({
      id: c, kind: "cluster", title: c, subtitle: "Kubernetes cluster",
      path: `/clusters/${c}`, score: s, health: "healthy",
    });
  }

  for (const t of traces.slice(0, 50)) {
    const s = scoreMatch(`${t.rootOperation} ${t.rootService} ${t.id}`, q);
    if (s > 0) items.push({
      id: t.id, kind: "trace", title: t.rootOperation,
      subtitle: `${t.rootService} · ${t.durationMs}ms`,
      path: `/traces/${t.id}`, score: s - 5, health: t.hasError ? "critical" : "healthy",
    });
  }

  for (const inc of incidents) {
    const s = scoreMatch(`${inc.title} ${inc.ruleName}`, q);
    if (s > 0) items.push({
      id: inc.id, kind: "incident", title: inc.title,
      subtitle: `${inc.ruleName} · ${inc.status}`,
      path: `/alerts?incident=${inc.id}`, score: s, health: inc.severity === "critical" ? "critical" : "warning",
    });
  }

  items.sort((a, b) => b.score - a.score);
  const top = items.slice(0, limit);
  const facets: Record<string, number> = {};
  for (const it of top) facets[it.kind] = (facets[it.kind] ?? 0) + 1;

  return { query: q, items: top, total: items.length, facets };
}
